import React from 'react';
import { MapPin as _MapPin, Tent, Mountain as _Mountain, ChevronRight, MapPin, Mountain } from 'lucide-react';

/**
 * ElevationProfileCard Component
 * Displays elevation profile, key locations, and nearby amenities
 * Specific to hiking/mountaineering services
 */
export default function ElevationProfileCard({
  elevationPoints = [],
  keyLocations = [],
  nearbyAmenities = [],
  meetingPointLink
}) {

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
      <h2 className="text-xl font-bold mb-4">Elevation Profile</h2>

      {/* SVG Elevation Chart */}
      <div className="relative w-full h-32 bg-gray-50 rounded-lg p-4">
        <svg className="w-full h-full" viewBox="0 0 400 100" preserveAspectRatio="none">
          <path
            d="M 0 80 Q 50 70 100 60 T 200 40 Q 250 30 300 25 L 300 25 Q 350 28 400 35"
            fill="none"
            stroke="#059669"
            strokeWidth="2"
          />
          <path
            d="M 0 80 Q 50 70 100 60 T 200 40 Q 250 30 300 25 L 300 25 Q 350 28 400 35 L 400 100 L 0 100 Z"
            fill="#d1fae5"
            opacity="0.5"
          />
        </svg>

        {/* Elevation labels */}
        {elevationPoints.length > 0 && (
          <>
            {elevationPoints.map((point, idx) => (
              <div
                key={idx}
                className={`absolute text-xs text-gray-600 ${point.position}`}
              >
                {point.elevation}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Location labels */}
      {elevationPoints.length > 0 && (
        <div className="flex justify-between text-xs text-gray-600 mt-2">
          {elevationPoints.map((point, idx) => (
            <span key={idx}>{point.label}</span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
        {/* Key Locations */}
        {keyLocations.length > 0 && (
          <div>
            <h3 className="font-semibold mb-3">Key Locations</h3>
            <div className="space-y-3">
              {keyLocations.map((location, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  {location.type === 'meeting' && (
                    <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  )}
                  {location.type === 'camp' && (
                    <Tent className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  )}
                  {location.type === 'summit' && (
                    <Mountain className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <div className="font-medium">{location.name}</div>
                    <div className="text-sm text-gray-600">{location.description}</div>
                    {location.details && (
                      <div className="text-xs text-gray-500 mt-1">{location.details}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Nearby Amenities */}
        {nearbyAmenities.length > 0 && (
          <div>
            <h3 className="font-semibold mb-3">Nearby Amenities</h3>
            <div className="space-y-2 text-sm text-gray-700">
              {nearbyAmenities.map((amenity, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full"></div>
                  <span>{amenity}</span>
                </div>
              ))}
            </div>

            {meetingPointLink && (
              <button
                onClick={() => window.open(meetingPointLink, '_blank')}
                className="mt-4 text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
              >
                <span>Get directions to meeting point</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
